import { useTranslation } from '@/src/i18n';
import { Ionicons } from '@expo/vector-icons';
import {
  ActivityIndicator,
  Image,
  KeyboardAvoidingView,
  Platform,
  ScrollView,
  Switch,
  Text,
  TextInput,
  TouchableOpacity,
  View,
} from 'react-native';
import { createStyles } from './CreateLeagueScreen.styles';

const POINT_PRESETS = [
  { key: 'f1', label: 'leagues.f1Style', points: [25, 18, 15, 12, 10, 8, 6, 4, 2, 1] },
  { key: 'linear', label: 'leagues.linear', points: [10, 8, 6, 5, 4, 3, 2, 1] },
  { key: 'winner', label: 'leagues.winnerTakesAll', points: [3, 1] },
  { key: 'custom', label: 'leagues.custom', points: [] },
];

export interface CreateLeagueScreenProps {
  name: string;
  setName: (value: string) => void;
  description: string;
  setDescription: (value: string) => void;
  logoUrl: string;
  pickLogo: () => void;
  uploadingLogo: boolean;
  selectedPreset: string;
  setSelectedPreset: (value: string) => void;
  customPoints: string;
  setCustomPoints: (value: string) => void;
  isPublic: boolean;
  setIsPublic: (value: boolean) => void;
  creating: boolean;
  handleCreate: () => void;
  handleCancel: () => void;
  colors: any;
}

export const CreateLeagueScreen = ({
  name,
  setName,
  description,
  setDescription,
  logoUrl,
  pickLogo,
  uploadingLogo,
  selectedPreset,
  setSelectedPreset,
  customPoints,
  setCustomPoints,
  isPublic,
  setIsPublic,
  creating,
  handleCreate,
  handleCancel,
  colors,
}: CreateLeagueScreenProps) => {
  const { t } = useTranslation();
  const styles = createStyles(colors);

  return (
    <KeyboardAvoidingView style={styles.container} behavior={Platform.OS === 'ios' ? 'padding' : undefined}>
      <ScrollView contentContainerStyle={styles.scrollContent}>
        <View style={styles.logoContainer}>
          <TouchableOpacity style={styles.logoCircle} onPress={pickLogo} disabled={uploadingLogo}>
            {uploadingLogo ? (
              <ActivityIndicator color={colors.primary} />
            ) : logoUrl ? (
              <Image source={{ uri: logoUrl }} style={styles.logoImage} />
            ) : (
              <Ionicons name="camera-outline" size={28} color={colors.textTertiary} />
            )}
          </TouchableOpacity>
          <Text style={styles.logoHint}>{t('leagues.logoOptional')}</Text>
        </View>

        <View style={styles.section}>
          <Text style={styles.label}>{t('leagues.leagueName')} *</Text>
          <TextInput
            style={styles.input}
            placeholder={t('leagues.leagueName')}
            placeholderTextColor={colors.textTertiary}
            value={name}
            onChangeText={setName}
            autoFocus
          />
        </View>

        <View style={styles.section}>
          <Text style={styles.label}>{t('leagues.description')}</Text>
          <TextInput
            style={[styles.input, styles.textArea]}
            placeholder={t('leagues.description')}
            placeholderTextColor={colors.textTertiary}
            value={description}
            onChangeText={setDescription}
            multiline
            numberOfLines={3}
          />
        </View>

        <View style={styles.section}>
          <Text style={styles.label}>{t('leagues.pointSystem')}</Text>
          <Text style={styles.sublabel}>{t('leagues.pointSystemDesc')}</Text>
          <View style={styles.presetGrid}>
            {POINT_PRESETS.map((preset) => (
              <TouchableOpacity
                key={preset.key}
                style={[
                  styles.presetCard,
                  selectedPreset === preset.key ? styles.presetCardSelected : styles.presetCardUnselected,
                ]}
                onPress={() => setSelectedPreset(preset.key)}
              >
                <View style={styles.presetInfo}>
                  <Text style={styles.presetTitle}>{t(preset.label)}</Text>
                  {preset.points.length > 0 && (
                    <Text style={styles.presetDesc}>{preset.points.join(', ')}</Text>
                  )}
                </View>
                {selectedPreset === preset.key && (
                  <View style={styles.checkBadge}>
                    <Ionicons name="checkmark" size={16} color={colors.primaryText} />
                  </View>
                )}
              </TouchableOpacity>
            ))}
          </View>
          {selectedPreset === 'custom' && (
            <TextInput
              style={[styles.input, { marginTop: 10 }]}
              placeholder={t('leagues.enterCustomPoints')}
              placeholderTextColor={colors.textTertiary}
              value={customPoints}
              onChangeText={setCustomPoints}
              keyboardType="numbers-and-punctuation"
            />
          )}
        </View>

        <View style={styles.section}>
          <Text style={styles.label}>{t('leagues.visibility')}</Text>
          <View style={[styles.visibilityRow, { backgroundColor: colors.card, borderColor: colors.border }]}>
            <View style={styles.visibilityInfo}>
              <Text style={[styles.visibilityTitle, { color: colors.text }]}>
                {isPublic ? t('leagues.public') : t('leagues.private')}
              </Text>
              <Text style={[styles.visibilityDesc, { color: colors.textSecondary }]}>
                {isPublic ? t('leagues.publicDesc') : t('leagues.privateDesc')}
              </Text>
            </View>
            <Switch
              value={isPublic}
              onValueChange={setIsPublic}
              trackColor={{ false: colors.border, true: colors.primary }}
              thumbColor={colors.primaryText}
            />
          </View>
        </View>

        <View style={styles.buttonRow}>
          <TouchableOpacity
            style={[styles.primaryButton, creating && styles.disabledButton]}
            onPress={handleCreate}
            disabled={creating}
          >
            {creating ? (
              <ActivityIndicator color={colors.primaryText} />
            ) : (
              <Text style={styles.primaryButtonText}>{t('common.create')}</Text>
            )}
          </TouchableOpacity>
          <TouchableOpacity style={styles.secondaryButton} onPress={handleCancel}>
            <Text style={styles.secondaryButtonText}>{t('common.cancel')}</Text>
          </TouchableOpacity>
        </View>
      </ScrollView>
    </KeyboardAvoidingView>
  );
};
